import type { IntakeSurface } from "@/components/estimates/consumer-intake-estimate/intake-flow-types"

// Consumer intake — shop-side configuration schema.
//
// Stored on `team.metadata.intake` (+ `metadata.branding`) via the REAL
// PUT /v1/teams/{teamId}/settings; read back on the public flow through the
// mock GET /public/shops/:id which serves a stripped view (no notify/sharedAt).
// The mock duplicates these defaults — keep backend-mock/src/services/
// intake-config.ts in sync with this file.

// metadata.branding — existing block; the consumer UI only tints the logo chip
// + a 2px hairline with primaryColor, never the structural palette.
export interface ShopBranding {
  primaryColor: string
  logoUrl: string | null
  logoInitials: string | null
  hours: string
}

export interface IntakeConfig {
  // Absent ⇒ true (same rule as marketingSupercharge).
  enabled: boolean
  // `{shop}` token is substituted with the team name at render time.
  welcome: { title: string; message: string }
  // Which skippable extras the consumer is offered.
  extras: {
    vehicle: boolean
    insurance: boolean
    situation: boolean
    booking: boolean
    uploadLater: boolean
  }
  // Name + phone are always required; email defaults to required.
  required: { email: boolean; vehicle: boolean }
  consent: { text: string; smsOptIn: boolean }
  // Stub-logged by the mock; stripped from the public read.
  notify: { emails: string[]; phones: string[]; onNewLead: boolean; onBooking: boolean }
  kiosk: { doneResetSeconds: number; idleResetSeconds: number }
  // First time any distribution action ran (link copied, QR downloaded, …).
  sharedAt: string | null
  // Null until the shop saves the section once.
  updatedAt: string | null
}

// Default consent text — flagged for Sam/legal sign-off.
export const INTAKE_DEFAULTS: IntakeConfig = {
  enabled: true,
  welcome: {
    title: "Let's see the damage.",
    message:
      "Send {shop} a few photos and we'll get your estimate started. Free, no obligation.",
  },
  extras: { vehicle: true, insurance: true, situation: true, booking: true, uploadLater: true },
  required: { email: true, vehicle: false },
  consent: {
    text: "By continuing you agree {shop} may text or email you about this request. Msg & data rates may apply. Reply STOP to opt out.",
    smsOptIn: true,
  },
  notify: { emails: [], phones: [], onNewLead: true, onBooking: true },
  kiosk: { doneResetSeconds: 20, idleResetSeconds: 120 },
  sharedAt: null,
  updatedAt: null,
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

// Per-section merge: every key in the defaults survives, raw values win only
// when they exist and have the same primitive type (a half-saved or older
// shape can never produce an undefined field downstream).
function section<T extends object>(defaults: T, raw: unknown): T {
  const out: Record<string, unknown> = { ...(defaults as Record<string, unknown>) }
  if (!isRecord(raw)) return out as T
  for (const key of Object.keys(defaults)) {
    const candidate = raw[key]
    if (candidate === undefined || candidate === null) continue
    if (typeof candidate !== typeof out[key]) continue
    if (Array.isArray(out[key]) !== Array.isArray(candidate)) continue
    out[key] = candidate
  }
  return out as T
}

// Deep-merge whatever sits on metadata.intake over the defaults.
export function resolveIntakeConfig(raw: unknown): IntakeConfig {
  const r = isRecord(raw) ? raw : {}
  return {
    enabled: typeof r.enabled === "boolean" ? r.enabled : true,
    welcome: section(INTAKE_DEFAULTS.welcome, r.welcome),
    extras: section(INTAKE_DEFAULTS.extras, r.extras),
    required: section(INTAKE_DEFAULTS.required, r.required),
    consent: section(INTAKE_DEFAULTS.consent, r.consent),
    notify: section(INTAKE_DEFAULTS.notify, r.notify),
    kiosk: section(INTAKE_DEFAULTS.kiosk, r.kiosk),
    sharedAt: typeof r.sharedAt === "string" ? r.sharedAt : null,
    updatedAt: typeof r.updatedAt === "string" ? r.updatedAt : null,
  }
}

// Substitute the `{shop}` token in welcome/consent copy.
export function withShopName(text: string, shopName: string): string {
  return text.replaceAll("{shop}", shopName)
}

// Where the public surfaces live for a shop. `origin` is publicOrigin() in
// the browser; the mock uses PUBLIC_APP_URL for the SMS continuation link.
export function intakeUrls(origin: string, shopId: number | string) {
  const base = `${origin}/go/${shopId}`
  return {
    link: base,
    qr: `${base}?src=qr`,
    sms: `${base}?src=sms`,
    embed: `${origin}/embed/${shopId}?src=embed`,
    widgetScript: `${origin}/etx-intake.js?v=1`,
    kiosk: `${origin}/kiosk/${shopId}`,
  }
}

export const SURFACE_LABEL: Record<IntakeSurface, string> = {
  link: "Link",
  qr: "QR code",
  sms: "Text link",
  embed: "Website",
  widget: "Website button",
  kiosk: "Kiosk",
}

// Origin used for anything the consumer/shop will open outside this tab
// (QR, snippets, share). Overridable so a LAN/dev host can mint scannable
// URLs without rebuilding.
export function publicOrigin(): string {
  const fromEnv = import.meta.env.VITE_PUBLIC_APP_ORIGIN
  return typeof fromEnv === "string" && fromEnv.length > 0
    ? fromEnv.replace(/\/+$/, "")
    : window.location.origin
}
